import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '@/lib/api.js';
import { useFmt } from '@/lib/useFmt.js';
import {
  Chip,
  EmptyState,
  GlassButton,
  LoadingState,
  MetricCard,
  PageHeader,
  Section,
  type MetricTone,
} from '@/components/ui/index.js';
import type { FeedbackListResponse, StatsResponse } from '@/lib/types.js';
import {
  DISCIPLINE_META,
  ITEMS,
  LANE_KIND,
  type Discipline,
} from '@/data/roadmap.js';

// 组织 = 4 建制线(独立业务线) + 4 横切门(跨线质量/合规闸口)。见 docs/工程/研发流程全景.md。
const BUILD_LINES: readonly Discipline[] = ['product', 'engineering', 'growth-ops', 'business'];
const CROSS_GATES: readonly Discipline[] = ['design', 'qa', 'legal', 'infra'];

interface DeptHealth {
  total: number;
  p0: number;
  doing: number;
  todo: number;
  done: number;
}

function deptHealth(d: Discipline): DeptHealth {
  const items = ITEMS.filter((i) => i.category === d);
  return {
    total: items.length,
    p0: items.filter((i) => i.priority === 'P0' && i.status !== 'done').length,
    doing: items.filter((i) => i.status === 'in_progress').length,
    todo: items.filter((i) => i.status === 'todo' || i.status === 'decision' || i.status === 'blocked').length,
    done: items.filter((i) => i.status === 'done').length,
  };
}

/** 一个部门卡:图标 + 名 + 建制/横切徽标 + 职责 + 工作健康度。点击进 /roadmap。 */
function DeptCard({ d, onOpen }: { d: Discipline; onOpen: () => void }): JSX.Element {
  const meta = DISCIPLINE_META[d];
  const kind = LANE_KIND[d];
  const h = deptHealth(d);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col gap-2 rounded-xl border border-white/[0.11] bg-[#212d42] p-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] transition-colors hover:border-white/20 hover:bg-[#26314a]"
    >
      <div className="flex items-center gap-2">
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[16px] font-semibold ring-1 ring-white/10"
          style={{ background: `${meta.color}33`, color: meta.color }}
          aria-hidden="true"
        >
          {meta.icon}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13.5px] font-semibold text-white">{meta.label}</span>
            <span
              className={`rounded px-1 py-px text-[9px] font-medium ${
                kind === '横切' ? 'bg-violet-500/15 text-violet-300' : 'bg-shark-500/15 text-shark-300'
              }`}
            >
              {kind === '横切' ? '横切门' : '建制线'}
            </span>
          </div>
          <p className="truncate text-[11px] text-slate-400">{meta.blurb}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        {h.p0 > 0 && <span className="font-semibold text-rose-300">P0 ×{h.p0}</span>}
        {h.doing > 0 && <span className="text-amber-300">进行 {h.doing}</span>}
        <span className="text-slate-400">待办 {h.todo}</span>
        <span className="text-emerald-400/80">已完成 {h.done}</span>
      </div>
    </button>
  );
}

interface TileDef {
  label: string;
  value: number;
  tone: MetricTone;
  to: string;
  unit: string;
}

export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StatsResponse['stats'] | null>(null);
  const [unreadFeedback, setUnreadFeedback] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const { fmtTime } = useFmt();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // Parallel: stats + a 1-row feedback query to surface unread_total.
      const [statsRes, feedbackRes] = await Promise.all([
        request<StatsResponse>('/admin/stats'),
        request<FeedbackListResponse>('/admin/feedback?limit=1&filter=unack').catch(() => null),
      ]);
      setStats(statsRes.stats);
      setUnreadFeedback(feedbackRes?.unread_total ?? null);
      setLastLoadedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const tiles: TileDef[] = stats !== null ? [
    { label: '总用户数', value: stats.total_users, tone: 'neutral', to: '/users', unit: '人' },
    { label: '活跃会员', value: stats.active_subscriptions, tone: 'info', to: '/users', unit: '人' },
    { label: '免费用户', value: stats.free_users, tone: 'neutral', to: '/users', unit: '人' },
    { label: '兑换码总数', value: stats.redeem_codes_total, tone: 'neutral', to: '/codes', unit: '个' },
    { label: '未使用兑换码', value: stats.redeem_codes_unused, tone: 'success', to: '/codes', unit: '个' },
    { label: '30 天付款订单', value: stats.orders_paid_30d, tone: 'info', to: '/orders', unit: '单' },
    { label: '30 天新增注册', value: stats.signups_30d, tone: 'neutral', to: '/users', unit: '人' },
  ] : [];

  // 待办列表 — 只在数字 > 0 时显示对应 chip, 不刷屏. 每条都点击直接跳到
  // 处理页. 顺序按重要度: 用户反馈 > 兑换码过期 > 访客未转化.
  const reminders: Array<{ key: string; label: string; count: number; to: string; tone: MetricTone }> = [];
  if (unreadFeedback !== null && unreadFeedback > 0) {
    reminders.push({
      key: 'feedback',
      label: '未处理反馈',
      count: unreadFeedback,
      to: '/feedback',
      tone: 'warning',
    });
  }
  if (stats !== null && stats.redeem_codes_expiring_7d > 0) {
    reminders.push({
      key: 'codes-expiring',
      label: '7 天内过期兑换码',
      count: stats.redeem_codes_expiring_7d,
      to: '/codes',
      tone: 'warning',
    });
  }
  if (stats !== null && stats.visitors_unconverted_7d > 0) {
    reminders.push({
      key: 'visitors-unconverted',
      label: '7 天未转化访客',
      count: stats.visitors_unconverted_7d,
      to: '/visitors',
      tone: 'info',
    });
  }

  return (
    <div>
      <PageHeader
        title="总览"
        subtitle="关键指标、待办提醒、最近活动"
        right={
          <div className="flex items-center gap-3 text-xs">
            {lastLoadedAt !== null && (
              <span className="text-slate-500">最后更新 {fmtTime(lastLoadedAt)}</span>
            )}
            <GlassButton size="sm" variant="ghost" onClick={(): void => void load()} disabled={loading}>
              {loading ? '刷新中…' : '↻ 刷新'}
            </GlassButton>
          </div>
        }
      />

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {/* Reminders bar */}
      {reminders.length > 0 && (
        <Section className="mb-5" muted>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-400 mr-2">⚠️ 待办</span>
            {reminders.map((r) => (
              <Chip
                key={r.key}
                active
                tone="amber"
                onClick={(): void => navigate(r.to)}
              >
                {r.label}
                <span className="ml-1.5 font-semibold">{r.count}</span>
              </Chip>
            ))}
          </div>
        </Section>
      )}

      {/* 组织 · 部门 —— 把"大公司很多部门"呈现在总览: 4 建制线 + 4 横切门 */}
      <Section className="mb-5">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white">组织 · 部门</h2>
            <p className="text-[11px] text-slate-400">
              4 条建制线(独立业务线)+ 4 道横切门(跨线质量/合规闸口)· 点开看各部门工作
            </p>
          </div>
          <GlassButton size="sm" variant="ghost" onClick={(): void => navigate('/roadmap')}>
            部门看板 →
          </GlassButton>
        </div>

        <div className="mb-2 text-[10.5px] font-medium uppercase tracking-wide text-shark-300/80">
          建制线 · 4 条独立业务线
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {BUILD_LINES.map((d) => (
            <DeptCard key={d} d={d} onOpen={(): void => navigate('/roadmap')} />
          ))}
        </div>

        <div className="mb-2 mt-4 text-[10.5px] font-medium uppercase tracking-wide text-violet-300/80">
          横切门 · 4 道跨线质量/合规闸口
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {CROSS_GATES.map((d) => (
            <DeptCard key={d} d={d} onOpen={(): void => navigate('/roadmap')} />
          ))}
        </div>
      </Section>

      {loading && stats === null ? (
        <LoadingState />
      ) : stats === null ? (
        <EmptyState title="暂无数据" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {tiles.map((t) => (
            <MetricCard
              key={t.label}
              label={t.label}
              value={t.value}
              unit={t.unit}
              tone={t.tone}
              onClick={(): void => navigate(t.to)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
